import { IGNORE_NODE_FIELDS, LOGAN_BASED_NODES_LIST } from '@pages/schemes/constants';
import { toJpeg, toPng, toSvg } from 'html-to-image';
import { Position } from 'reactflow';
import { getDeepDifferentFields } from '@shared/utils/utilsOld';
import { INITIAL_SCHEME_DATA } from '@shared/constants/initialDataConstants';

export const makeNodesOrEdgesUnselected = (nodesOrEdgesArray) => {
    return nodesOrEdgesArray.reduce((acc, element) => {
        if (element?.data?.selectedHandleId) {
            element.data.selectedHandleId = null; // remove handle selection in order to not store it in DB
        }

        if (element.animated && element.selected) {
            acc.push({ ...element, selected: false, animated: false });
        } else if (element.selected) {
            acc.push({ ...element, selected: false });
        } else {
            acc.push(element);
        }

        return acc;
    }, []);
};

export function validateReactflowConfig(config) {
    if (!config.hasOwnProperty('nodes')) {
        return ["Missing 'nodes' property."];
    }

    // Check if 'nodes' property is an array
    if (!Array.isArray(config.nodes)) {
        return ["'nodes' property should be an array."];
    }

    if (!config.hasOwnProperty('edges')) {
        return ["Missing 'edges' property."];
    }

    // Check if 'edges' property is an array
    if (!Array.isArray(config.edges)) {
        return ["'edges' property should be an array."];
    }

    const errors = [];

    // Check if all required fields exist in the 'nodes' array
    const requiredNodeFields = ['id', 'type', 'data'];

    for (const [index, node] of config.nodes.entries()) {
        const missingFields = requiredNodeFields.filter((field) => !node.hasOwnProperty(field));
        if (missingFields.length > 0) {
            errors.push(`Missing fields [${missingFields}] in node with index '${index}'`);
        }
        // Check if 'ports' property exists in the 'data' object of each node
        if (node.data && (!node.data.hasOwnProperty('ports') || !Array.isArray(node.data.ports))) {
            errors.push(`Missing or invalid 'ports' property in node with id '${node.id}'.`);
        }
    }

    // Check if all required fields exist in the 'edges' array
    const requiredEdgeFields = ['source', 'sourceHandle', 'target', 'targetHandle', 'id'];
    for (const [index, edge] of config.edges.entries()) {
        const missingFields = requiredEdgeFields.filter((field) => !edge.hasOwnProperty(field));
        if (missingFields.length > 0) {
            errors.push(`Missing fields  ${missingFields.join(', ')} in edge with index '${index}'`);
        }
    }

    return errors.length === 0 ? null : errors;
}

export function addRequiredInternalFieldsToJsonConfig(jsonConfig) {
    jsonConfig.nodes.forEach((nodeObject, index) => {
        if (!nodeObject.position) {
            nodeObject.position = { x: index * 10, y: index * 10 };
        }
    });

    return jsonConfig;
}

function filterUselessNodes(node) {
    // we don't want to add minimap, controls, etc. to the image
    return !(
        node?.classList?.contains('react-flow__minimap') ||
        node?.classList?.contains('react-flow__controls') ||
        node?.classList?.contains('rotateHandle')
    );
}

export const getPngPreviewFile = async (fileName, returnBlob) => {
    await makeFitView();

    const reactflowComponent = document.querySelector('.react-flow__pane');

    if (!reactflowComponent) {
        return;
    }

    const isFirefox = navigator.userAgent.toLowerCase().includes('firefox');

    return toPng(reactflowComponent, {
        filter: filterUselessNodes,
        // skipAutoScale: true
        skipFonts: isFirefox,
    }).then((dataUrl) => {
        const byteString = atob(dataUrl.split(',')[1]);
        const mimeString = dataUrl.split(',')[0].split(':')[1].split(';')[0];
        const arrayBuffer = new ArrayBuffer(byteString.length);
        const uint8Array = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteString.length; i++) {
            uint8Array[i] = byteString.charCodeAt(i);
        }

        const blob = new Blob([arrayBuffer], { type: mimeString });

        if (returnBlob) {
            return blob;
        }

        return new File([blob], `${fileName}.png`, { type: mimeString });
    });
};

// returns the position (top,right,bottom or right) passed node compared to the intersection point
export function getEdgePosition(node, intersectionPoint) {
    const n = { ...node.positionAbsolute, ...node };
    const nx = Math.round(n.x);
    const ny = Math.round(n.y);
    const px = Math.round(intersectionPoint.x);
    const py = Math.round(intersectionPoint.y);

    if (px <= nx + 1) {
        return Position.Left;
    }
    if (px >= nx + n.width - 1) {
        return Position.Right;
    }
    if (py <= ny + 1) {
        return Position.Top;
    }
    if (py >= n.y + n.height - 1) {
        return Position.Bottom;
    }

    return Position.Top;
}

// returns the parameters (sx, sy, tx, ty, sourcePos, targetPos) you need to create an edge
// this helper function returns the intersection point
// of the line between the center of the intersectionNode and the target node
function getNodeIntersection(intersectionNode, targetNode) {
    // https://math.stackexchange.com/questions/1724792/an-algorithm-for-finding-the-intersection-point-between-a-center-of-vision-and-a

    const {
        width: intersectionNodeWidth,
        height: intersectionNodeHeight,
        position: intersectionNodePosition,
    } = intersectionNode;

    const { width: targetNodeWidth, height: targetNodeHeight, positionAbsolute: targetNodePosition } = targetNode;

    const w = (intersectionNodeWidth ?? 0) / 2;
    const h = (intersectionNodeHeight ?? 0) / 2;

    const x2 = intersectionNodePosition.x + w;
    const y2 = intersectionNodePosition.y + h;
    const x1 = targetNodePosition.x + (targetNodeWidth ?? 0) / 2;
    const y1 = targetNodePosition.y + (targetNodeHeight ?? 0) / 2;

    const xx1 = (x1 - x2) / (2 * w) - (y1 - y2) / (2 * h);
    const yy1 = (x1 - x2) / (2 * w) + (y1 - y2) / (2 * h);
    const a = 1 / (Math.abs(xx1) + Math.abs(yy1));
    const xx3 = a * xx1;
    const yy3 = a * yy1;
    const x = w * (xx3 + yy3) + x2;
    const y = h * (-xx3 + yy3) + y2;

    return { x, y };
}

export function getEdgeParams(source, target) {
    const sourceIntersectionPoint = getNodeIntersection(source, target);
    const targetIntersectionPoint = getNodeIntersection(target, source);

    const sourcePos = getEdgePosition(source, sourceIntersectionPoint);
    const targetPos = getEdgePosition(target, targetIntersectionPoint);

    return {
        sx: sourceIntersectionPoint.x,
        sy: sourceIntersectionPoint.y,
        tx: targetIntersectionPoint.x,
        ty: targetIntersectionPoint.y,
        sourcePos,
        targetPos,
    };
}

export const nodesSelectionActiveSelector = (state) => state.nodesSelectionActive || false;
export const nodesCanBeMovedByKeyboardSelector = (state) => {
    if (state.nodesSelectionActive) {
        return true;
    }

    const nodeObjects = Array.from(state.nodeInternals.values());
    const selectedNodeWithSelectedHandle = nodeObjects.find(
        (nodeObject) => nodeObject.selected && nodeObject.data.selectedHandleId,
    );

    return !selectedNodeWithSelectedHandle;
};
export const removeUselessEdges = (changes, nodes, setEdges) => {
    if (changes.length === 1) {
        const changedNodeId = changes[0].id;
        const changedNode = nodes.find((node) => node.id === changedNodeId);

        if (!changedNode) {
            return;
        }

        setEdges((eds) => {
            return eds.filter((ed) => {
                return !(
                    ed.source === changedNode.id &&
                    !changedNode.data.ports.find((port) => port['name'] === ed.sourceHandle)
                );
            });
        });
    }
};
export const makeCorrectLoganBasedNodeRoles = (nodes, updatedNodeId) => {
    let newNodes = structuredClone(nodes);

    for (const loganBasedNode of LOGAN_BASED_NODES_LIST) {
        if (
            !nodes.find(
                (node) => node.type === loganBasedNode && node.id !== updatedNodeId && node.data.role === 'regular',
            )
        ) {
            let regularNodeExists = false;

            newNodes = nodes.map((node) => {
                if (node.type === loganBasedNode && node.id !== updatedNodeId && !regularNodeExists) {
                    node.data.role = 'regular';
                    regularNodeExists = true;
                    return node;
                }

                return node;
            });
        }
    }

    return newNodes;
};

export async function makeFitView() {
    const fitViewComponent = document.querySelector('.react-flow__controls-fitview');

    if (!fitViewComponent) {
        return;
    }

    const fitViewPromise = new Promise((resolve) => {
        fitViewComponent.addEventListener('click', resolve);
    });
    fitViewComponent.click();
    await fitViewPromise;
}

export const getJpegPreview = async () => {
    await makeFitView();
    toJpeg(document.querySelector('.react-flow__pane'), {
        quality: 1,
        filter: filterUselessNodes,
        backgroundColor: 'white',
    }).then(function (dataUrl) {
        const link = document.createElement('a');
        link.download = 'scheme.jpg';
        link.href = dataUrl;
        link.click();
    });
};
export const getSvgPreview = async () => {
    await makeFitView();
    toSvg(document.querySelector('.react-flow__pane'), { filter: filterUselessNodes, backgroundColor: 'white' }).then(
        function (dataUrl) {
            const link = document.createElement('a');
            link.download = 'scheme.svg';
            link.href = dataUrl;
            link.click();
        },
    );
};

export function getHandlePosition(initialPos, rotation) {
    //maps are used for store match between rotation and needed position
    const leftMap = { 90: Position.Bottom, 180: Position.Right, 270: Position.Top };
    const rightMap = { 90: Position.Top, 180: Position.Left, 270: Position.Bottom };
    const topMap = { 90: Position.Left, 180: Position.Bottom, 270: Position.Right };
    const bottomMap = { 90: Position.Right, 180: Position.Top, 270: Position.Left };

    let mapDegreeToPosition;
    switch (initialPos) {
        case Position.Left:
            mapDegreeToPosition = leftMap;
            break;
        case Position.Right:
            mapDegreeToPosition = rightMap;
            break;
        case Position.Top:
            mapDegreeToPosition = topMap;
            break;
        case Position.Bottom:
            mapDegreeToPosition = bottomMap;
            break;
        default:
            break;
    }

    if (!mapDegreeToPosition || !rotation) {
        return initialPos;
    }

    let resultPosition;
    // every 90deg need to change position
    if (rotation < 45 || rotation > 315) {
        resultPosition = initialPos;
    } else if (rotation < 135) {
        resultPosition = mapDegreeToPosition[270];
    } else if (rotation < 225) {
        resultPosition = mapDegreeToPosition[180];
    } else if (rotation < 315) {
        resultPosition = mapDegreeToPosition[90];
    }

    return resultPosition;
}

export function findOptimalHandlePosition(data) {
    const portObjects = data.ports_positions;

    const positionsCount = { top: 0, right: 0, bottom: 0, left: 0 };

    // Count the occurrences of each position in portObjects
    for (const portId in portObjects) {
        const portPosition = portObjects[portId].reactflow_position;
        positionsCount[portPosition]++;
    }

    // Find the position with the lowest count
    let leastUsedPosition = 'top'; // Initialize with a default position
    let leastCount = Number.MAX_SAFE_INTEGER;

    for (const position in positionsCount) {
        const count = positionsCount[position];
        if (count < leastCount) {
            leastCount = count;
            leastUsedPosition = position;
        }
    }

    return leastUsedPosition;
}

export function getSchemeDataWithoutMetadata(data) {
    let dataCopy;
    if (!data) {
        return null;
    }

    dataCopy = structuredClone(data);

    return {
        ...dataCopy,
        json_config: {
            nodes: makeNodesOrEdgesUnselected(dataCopy.json_config.nodes),
            edges: structuredClone(dataCopy.json_config.edges).map((item) => {
                if (item.hasOwnProperty('selected')) {
                    delete item.selected;
                }
                if (item.hasOwnProperty('animated')) {
                    delete item.animated;
                }
                return item;
            }),
        },
    };
}

export const cleanNodes = (nodes) =>
    nodes.map((node) => {
        const copy = structuredClone(node);
        IGNORE_NODE_FIELDS.forEach((field) => delete copy[field]);
        return copy;
    });
export const cleanObjectForComparison = (obj) => {
    if (!obj) return obj;

    const copy = structuredClone(obj);

    return {
        ...copy,
        json_config: copy.json_config
            ? {
                  ...copy.json_config,
                  nodes: cleanNodes(copy.json_config.nodes || []),
                  edges: copy.json_config.edges || [],
              }
            : INITIAL_SCHEME_DATA.json_config,
    };
};
export const compareObjectsWithNodes = (dataLocal, dataActual) => {
    if (!dataLocal || !dataActual) return true;

    if (!dataLocal?.json_config || !dataActual?.json_config) {
        const dataLocalWithoutConfig = structuredClone(dataLocal);
        const dataActualWithoutConfig = structuredClone(dataActual);
        delete dataLocalWithoutConfig.json_config;
        delete dataActualWithoutConfig.json_config;

        const diff = getDeepDifferentFields(dataLocalWithoutConfig, dataActualWithoutConfig);
        return Object.keys(diff).length === 0;
    }

    const cleanDataLocal = cleanObjectForComparison(dataLocal);
    const cleanDataActual = cleanObjectForComparison(dataActual);

    const diff = getDeepDifferentFields(cleanDataLocal, cleanDataActual);
    return Object.keys(diff).length === 0;
};
